import { useEffect, useState } from 'react';
import { settingsStorage } from '../services/settingsStorage';

/**
 * Hook pour migrer les paramètres de localStorage vers IndexedDB
 * Exécuté une seule fois au démarrage de l'application
 */
export const useSettingsMigration = () => {
  const [isMigrated, setIsMigrated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const migrateSettings = async () => {
      try {
        // Vérifier si la migration a déjà été effectuée
        const migrationKey = 'settings_migration_completed';
        const migrationCompleted = localStorage.getItem(migrationKey);

        if (migrationCompleted === 'true') {
          setIsMigrated(true);
          setIsLoading(false);
          return;
        }

        // Migrer printSettings
        const oldPrintSettings = localStorage.getItem('printSettings');
        if (oldPrintSettings && ['manual', 'auto', 'none'].includes(oldPrintSettings)) {
          const currentPrintSettings = await settingsStorage.getPrintSettings();
          // Ne migrer que si IndexedDB a la valeur par défaut
          if (currentPrintSettings === 'manual' && oldPrintSettings !== 'manual') {
            await settingsStorage.savePrintSettings(oldPrintSettings as 'manual' | 'auto' | 'none');
          }
        }

        // Migrer baseLanguage
        const oldBaseLanguage = localStorage.getItem('baseLanguage');
        if (oldBaseLanguage) {
          const currentBaseLanguage = await settingsStorage.getBaseLanguage();
          // Ne migrer que si IndexedDB a la valeur par défaut
          if (currentBaseLanguage === 'fr' && oldBaseLanguage !== 'fr') {
            await settingsStorage.saveBaseLanguage(oldBaseLanguage);
          }
        }

        // Migrer googleApiKey
        const oldGoogleApiKey = localStorage.getItem('googleApiKey');
        if (oldGoogleApiKey) {
          const currentGoogleApiKey = await settingsStorage.getGoogleApiKey();
          // Ne migrer que si IndexedDB est vide
          if (!currentGoogleApiKey && oldGoogleApiKey) {
            await settingsStorage.saveGoogleApiKey(oldGoogleApiKey);
          }
        }

        // Migrer isTranslationEnabled
        const oldTranslationEnabled = localStorage.getItem('isTranslationEnabled');
        if (oldTranslationEnabled !== null) {
          const currentTranslationEnabled = await settingsStorage.getTranslationEnabled();
          const newValue = oldTranslationEnabled !== 'false';
          // Ne migrer que si les valeurs diffèrent
          if (currentTranslationEnabled !== newValue) {
            await settingsStorage.saveTranslationEnabled(newValue);
          }
        }

        // Migrer paymentSettings
        const oldPaymentSettings = localStorage.getItem('paymentSettings');
        if (oldPaymentSettings) {
          try {
            const parsedPaymentSettings = JSON.parse(oldPaymentSettings);
            const currentPaymentSettings = await settingsStorage.getPaymentSettings();
            // Ne migrer que si les valeurs diffèrent
            if (
              parsedPaymentSettings.enableCardPayment !== currentPaymentSettings.enableCardPayment ||
              parsedPaymentSettings.enableCashPayment !== currentPaymentSettings.enableCashPayment
            ) {
              await settingsStorage.savePaymentSettings(parsedPaymentSettings);
            }
          } catch (error) {
          }
        }

        // Marquer la migration comme terminée
        localStorage.setItem(migrationKey, 'true');

        setIsMigrated(true);
      } catch (error) {
        // Même en cas d'erreur, on marque comme migré pour éviter de bloquer l'app
        localStorage.setItem('settings_migration_completed', 'true');
        setIsMigrated(true);
      } finally {
        setIsLoading(false);
      }
    };

    migrateSettings();
  }, []);

  return { isMigrated, isLoading };
};
